import { useState } from 'react';
import type { Nullable } from 'corgi-types';

import { MessageType } from 'common/enums/Alert';
import { executeQuery } from 'common/gql';
import type { DispatchState } from 'common/types/React';
import { FieldName } from '../enums/form';
import giftCardStatusQuery from '../gql/gift-card-status.gql';
import type { CheckBalanceFormAlert } from '../types/alert';
import type { GiftCardStatus } from '../types/gift-card';
import { getCheckBalanceFormErrorMessage } from '../utils/handle-errors';

type FormData = {
  [key in FieldName]: string;
};

interface GiftCardStatusQueryResponse {
  data: GiftCardStatus;
}

interface GiftCardStatusQueryVariables {
  code: string;
  pin: string;
}

interface UseSubmitForm {
  setAlert: DispatchState<CheckBalanceFormAlert>;
  setGiftCardStatus: DispatchState<Nullable<GiftCardStatus>>;
}

const getErrorAlert = (message: Nullable<string>): CheckBalanceFormAlert =>
  message ? { type: MessageType.Error, message } : null;

export const useSubmitForm = ({ setAlert, setGiftCardStatus }: UseSubmitForm) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (formData: FormData) => {
    setAlert(null);
    setIsLoading(true);

    try {
      const variables = {
        code: formData[FieldName.Code] ?? '',
        pin: formData[FieldName.Pin] ?? '',
      };

      const { data } = await executeQuery<
        GiftCardStatusQueryResponse,
        GiftCardStatusQueryVariables
      >({
        query: giftCardStatusQuery,
        variables,
        useApq: true,
      });

      setGiftCardStatus(data);
      setIsSuccess(true);
    } catch (error) {
      setAlert(getErrorAlert(getCheckBalanceFormErrorMessage(error)));
    }

    setIsLoading(false);
  };

  return { handleSubmit, isLoading, isSuccess };
};
